import MiniSearch, { type SearchResult } from 'minisearch';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import type { FaqsItem } from '../components/domain/Faqs/Faqs.model';

/** Matches scoring below this fraction of the top score are dropped as noise. */
const RELATIVE_SCORE_FLOOR = 0.3;
const MAX_RESULTS = 5;

export interface FaqSearchResult {
  item: FaqsItem;
  score: number;
}

interface FaqDocument {
  id: number;
  question: string;
  answer: string;
  keywords: string;
}

/**
 * Ranks FAQ items against a free-text query using BM25 relevance.
 * The active locale drives word segmentation, which matters for scripts without spaces.
 * @param items Items to index. The index is rebuilt whenever they or the locale change.
 * @param query Free-text query. An empty or blank query yields no results.
 * @returns Matches ordered by descending relevance.
 */
export function useFaqSearch(
  items: MaybeRefOrGetter<FaqsItem[] | undefined>,
  query: MaybeRefOrGetter<string>,
) {
  const { locale } = useI18n();

  const index = computed(() => buildIndex(toValue(items) || [], locale.value));

  const results = computed<FaqSearchResult[]>(() => {
    const term = toValue(query).trim();
    if (!term) return [];
    return rankMatches(index.value.search(term), toValue(items) || []);
  });

  return { results };
}

function buildIndex(items: FaqsItem[], locale: string) {
  const index = new MiniSearch<FaqDocument>({
    idField: 'id',
    fields: ['question', 'keywords', 'answer'],
    tokenize: createTokenizer(locale),
    searchOptions: {
      boost: { question: 3, keywords: 2 },
      prefix: true,
      fuzzy: 0.2,
      // A natural language question must not require every one of its terms to be present.
      combineWith: 'OR',
    },
  });
  index.addAll(items.map(toDocument));
  return index;
}

function toDocument(item: FaqsItem, id: number): FaqDocument {
  return {
    id,
    question: item.question,
    answer: item.answer,
    keywords: (item.keywords || []).join(' '),
  };
}

/** Segments words for scripts that don't separate them with spaces (Chinese, Japanese, Thai). */
function createTokenizer(locale: string): (text: string) => string[] {
  const segmenter = createSegmenter(locale);
  if (!segmenter) return splitOnSeparators;

  return text => [...segmenter.segment(text)]
    .filter(part => part.isWordLike)
    .map(part => part.segment);
}

/** Missing on older engines, and the constructor rejects a malformed locale tag. */
function createSegmenter(locale: string): Intl.Segmenter | undefined {
  if (!('Segmenter' in Intl)) return undefined;

  try {
    return new Intl.Segmenter(locale, { granularity: 'word' });
  } catch {
    return undefined;
  }
}

function splitOnSeparators(text: string): string[] {
  return text.split(/[\n\r\p{Z}\p{P}]+/u).filter(Boolean);
}

/**
 * Drops weak matches, which an OR combined query produces in bulk from common words.
 * @param matches MiniSearch results, already ordered by descending score.
 * @param items Indexed items, addressed by the document id.
 */
function rankMatches(matches: SearchResult[], items: FaqsItem[]): FaqSearchResult[] {
  const topScore = matches[0]?.score;
  if (!topScore) return [];

  return matches
    .filter(match => match.score >= topScore * RELATIVE_SCORE_FLOOR)
    .slice(0, MAX_RESULTS)
    .flatMap((match) => {
      // The index is rebuilt from the same items, so a miss means they changed mid-lookup.
      const item = items[match.id as number];
      return item ? [{ item, score: match.score }] : [];
    });
}
